import { motion } from "framer-motion";
import { Check } from "lucide-react";

import type { NotificationAction } from "@/store/useNotificationStore";

interface ActionNotificationProps {
  message: string;
  action: NotificationAction;
  onClose: () => void;
}

/**
 * A toast that carries one button, for a write the teacher can still take back.
 *
 * Written here rather than as a prop on the shared `Notification` from
 * `@ever-tutor/et-ui`: that component ships from another package, so an action
 * slot cannot be added to it from this repo. Only notifications that pass an
 * `action` render through this -- every other toast keeps the shared component
 * untouched.
 */
const ActionNotification = ({
  message,
  action,
  onClose,
}: ActionNotificationProps) => (
  <motion.div
    initial={{ opacity: 0, y: -20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
    className="flex w-[380px] max-w-full items-start gap-3 rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] p-4 shadow-lg"
  >
    <span
      aria-hidden="true"
      className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#EEF0E3] text-[#28543D]"
    >
      <Check size={13} strokeWidth={3} />
    </span>
    <p
      role="status"
      className="min-w-0 flex-1 text-sm leading-6 text-[#294B39]"
    >
      {message}
    </p>
    <button
      type="button"
      onClick={() => {
        action.onClick();
        // The offer is spent either way. Leaving the toast up invites a second
        // click, which would send the reversal twice.
        onClose();
      }}
      className="shrink-0 self-center rounded-[6px] border border-[#D4D3C5] bg-transparent px-3 py-1.5 text-sm font-bold text-[#28543D] transition-colors hover:bg-[#EEF0E3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#28543D]/40"
    >
      {action.label}
    </button>
  </motion.div>
);

export default ActionNotification;
